// API utility for handling base URLs in development vs production
// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: window?.location?.pathname || '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

const BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5000' 
  : '';

// Force HTTP for development to avoid HTTPS redirects
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5000';
  }
  return '';
};

// Global error suppression for auth endpoints
let suppressAuthErrors = false;

// Function to suppress auth errors globally
export const suppressAuthErrorLogging = () => {
  suppressAuthErrors = true;
};

// Function to restore auth error logging
export const restoreAuthErrorLogging = () => {
  suppressAuthErrors = false;
};

// Fetch with timeout using AbortController
const fetchWithTimeout = (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const opts = { ...options, signal: controller.signal };
  return fetch(url, opts).finally(() => clearTimeout(id));
};

/**
 * Centralized fetch function that handles base URL automatically
 * @param {string} endpoint - The API endpoint (e.g., '/api/auth/login')
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
  // Disallow absolute URLs to prevent accidental cross-origin calls
  if (typeof endpoint !== 'string' || endpoint.length === 0) {
    safeReportError('api_invalid_endpoint');
    throw new Error('api_invalid_endpoint');
  }
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    safeReportError('api_absolute_url_disallowed', { endpoint });
    throw new Error('api_absolute_url_disallowed');
  }
  // Normalize endpoint to start with '/'
  const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${getBaseUrl()}${normalized}`;

  try {
    const response = await fetchWithTimeout(url, options);
    return response;
  } catch (error) {
    if (suppressAuthErrors && normalized.includes('/auth/')) {
      throw error;
    }
    if (error?.name === 'AbortError') {
      // Do not report expected aborts (timeouts/cancellations)
      throw error;
    }
    safeReportError('api_fetch_failed', { endpoint: normalized });
    throw error;
  }
};

/**
 * Helper function for GET requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiGet = async (endpoint, options = {}) => {
  // Optional optimization: short-circuit /auth/me when clearly logged out
  try {
    if (endpoint.includes('/auth/me')) {
      const hasStoredUser = localStorage.getItem('auth-storage');
      if (!hasStoredUser) {
        return {
          ok: false,
          status: 401,
          json: async () => ({ error: 'Not authenticated' }),
          headers: new Headers(),
          statusText: 'Unauthorized',
          type: 'default',
          url: endpoint
        };
      }
    }
  } catch (_) {}

  try {
    const response = await apiFetch(endpoint, {
      method: 'GET',
      credentials: 'include',
      ...options
    });
    
    // Completely silent for 401 errors on auth endpoints
    if (response.status === 401 && endpoint.includes('/auth/')) {
      return response;
    }
    
    return response;
  } catch (error) {
    // Completely silent for auth endpoint errors
    if (endpoint.includes('/auth/')) {
      throw error;
    }
    // Silent for AbortError (expected when requests are cancelled)
    if (error.name === 'AbortError') {
      throw error;
    }
    // API GET error
    safeReportError('api_get_failed', { endpoint });
    throw error;
  }
};

/**
 * Helper function for POST requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPost = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  try {
    return await apiFetch(endpoint, fetchOptions);
  } catch (error) {
    if (!endpoint.includes('/auth/') && error?.name !== 'AbortError') {
      safeReportError('api_post_failed', { endpoint });
    }
    throw error;
  }
};

/**
 * Helper function for PUT requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPut = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'PUT',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  try {
    return await apiFetch(endpoint, fetchOptions);
  } catch (error) {
    if (!endpoint.includes('/auth/') && error?.name !== 'AbortError') {
      safeReportError('api_put_failed', { endpoint });
    }
    throw error;
  }
};

/**
 * Helper function for PATCH requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPatch = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'PATCH',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  try {
    return await apiFetch(endpoint, fetchOptions);
  } catch (error) {
    if (!endpoint.includes('/auth/') && error?.name !== 'AbortError') {
      safeReportError('api_patch_failed', { endpoint });
    }
    throw error;
  }
};

/**
 * Helper function for DELETE requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiDelete = async (endpoint, options = {}) => {
  try {
    return await apiFetch(endpoint, {
      method: 'DELETE',
      credentials: 'include',
      ...options
    });
  } catch (error) {
    if (!endpoint.includes('/auth/') && error?.name !== 'AbortError') {
      safeReportError('api_delete_failed', { endpoint });
    }
    throw error;
  }
};

/**
 * Helper function to get proper image URL for development vs production
 * @param {string} imagePath - The image path (e.g., 'filename.jpg' or '/uploads/filename.jpg')
 * @returns {string} - The complete image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) {
    // Allow only http/https protocols; prefer https in production
    try {
      const url = new URL(imagePath);
      if (url.protocol === 'http:' && window.location.protocol === 'https:') {
        // Attempt to upgrade to https
        url.protocol = 'https:';
        return url.toString();
      }
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return imagePath;
      }
      safeReportError('image_url_disallowed_protocol');
      return '';
    } catch (_) {
      return '';
    }
  }
  
  // If it starts with /uploads/, use the base URL
  if (imagePath.startsWith('/uploads/')) {
    return `${BASE_URL}${imagePath}`;
  }
  
  // If it's just a filename, assume it's in uploads
  return `${BASE_URL}/uploads/${imagePath}`;
};

// Export BASE_URL for direct use if needed
export { BASE_URL }; 